import { useCallback, useState } from 'react';

import { readPersistedValue, writePersistedValue } from './usePersistedState';

/**
 * A persisted value read once on mount, plus a writer that never re-renders.
 *
 * For a value handed to a child as its *initial* prop, where the child owns the
 * live value from then on. `usePersistedState` is wrong for that shape: its
 * setter re-renders this component, and when the child reports changes through a
 * continuous callback (kai-shiki `WorkspaceLayout`'s `onResize` fires per mouse
 * move) that is a re-render per frame of a drag, recreating every element this
 * component passes down — for a value the child is already tracking itself.
 *
 * `key` follows the same convention as {@link usePersistedState}: unique per
 * value per page, read once on mount.
 */
export const usePersistedInitialValue = <T>(
  key: string,
  defaultValue: T,
): [T, (value: T) => void] => {
  const [initialValue] = useState<T>(() => readPersistedValue(key, defaultValue));
  const persistValue = useCallback((value: T) => writePersistedValue(key, value), [key]);

  return [initialValue, persistValue];
};
